import { useCallback } from "react";

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly requestId?: string,
    public readonly body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiClient {
  apiFetch<T>(path: string, init?: RequestInit): Promise<T>;
}

async function doFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!headers.has("content-type") && init?.body !== undefined) {
    headers.set("content-type", "application/json");
  }
  const res = await fetch(path, { ...init, credentials: "include", headers });
  const text = await res.text();
  // Don't assume the body is JSON: edge/proxy 5xx pages, the SPA HTML fallback,
  // and gateway errors return HTML. A raw JSON.parse there would throw an opaque
  // SyntaxError instead of an ApiError carrying the real status.
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = null;
    }
  }
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string; requestId?: string } })?.error ?? {};
    throw new ApiError(err.code ?? "UNKNOWN", err.message ?? res.statusText, res.status, err.requestId, body);
  }
  return body as T;
}

export function useApi(): ApiClient {
  const apiFetch = useCallback(
    async <T>(path: string, init?: RequestInit): Promise<T> => doFetch<T>(path, init),
    []
  );
  return { apiFetch };
}

/**
 * Non-hook variant for cases where a hook can't run (e.g. event handlers in
 * test helpers). Token param retained for backward compat but ignored — cookies handle auth.
 */
export async function apiFetchWithToken<T>(_token: string | null, path: string, init?: RequestInit): Promise<T> {
  return doFetch<T>(path, init);
}
